import type { ReactNode } from 'react'
import React from 'react'

import {
  Accordion,
  BaseContent,
  ModalBox,
  useAccordionState,
  useModalContext,
} from '@fourthwall/components'
import { pluralize } from '@fourthwall/utils/dist/string'
import { reverse, routing } from '@utils/routing'

import {
  ProductsStatusModalAccordionHeading,
  type ProductsStatusModalAccordionHeadingProps,
} from '../ProductsStatusModalAccordionHeading/ProductsStatusModalAccordionHeading'
import * as S from './ProductsStatusModal.styled'

type Variant = {
  name?: string
  description?: ReactNode
}

type Product = {
  id: string
  imageSrc?: string
  name: string
  description?: string
  tag?: ProductsStatusModalAccordionHeadingProps['tag']
  variants: Variant[]
}

export type ProductsStatusModalProps = {
  title: string
  products: Product[]
}

export const ProductsStatusModal = ({ title, products }: ProductsStatusModalProps) => {
  const { close } = useModalContext()
  const { activeState, toggle } = useAccordionState(
    products.map(() => false),
    { allowZeroActive: true },
  )

  return (
    <ModalBox title={title} onClose={close}>
      <S.ProductsContainer>
        <Accordion>
          {products.map((product, index) => {
            const hasVariants = !!product.variants.length

            const getDescription = () => {
              if (typeof product.description === 'string') {
                return product.description
              }

              if (hasVariants) {
                return `${product.variants.length} ${pluralize(product.variants.length, 'variant')}`
              }
            }

            return (
              <Accordion.Item
                key={product.id}
                heading={
                  <ProductsStatusModalAccordionHeading
                    imageSrc={product.imageSrc}
                    name={product.name}
                    tag={product.tag}
                    description={getDescription()}
                    url={reverse(routing.products.all.product.self, { productId: product.id })}
                    onClick={hasVariants ? () => toggle(index) : undefined}
                  />
                }
                spacing="tiny"
                expanded={activeState[index]}
                onChange={hasVariants ? () => toggle(index) : undefined}
              >
                {hasVariants && (
                  <S.VariantsContainer>
                    <Accordion>
                      {product.variants.map((variant) => (
                        <Accordion.Item
                          heading={variant.name && <S.VariantName>{variant.name}</S.VariantName>}
                          spacing="small"
                          expanded
                        >
                          {variant.description && (
                            <BaseContent appearance="secondary" size="xsmall">
                              {variant.description}
                            </BaseContent>
                          )}
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </S.VariantsContainer>
                )}
              </Accordion.Item>
            )
          })}
        </Accordion>
      </S.ProductsContainer>
    </ModalBox>
  )
}
